import React from 'react'
import { useTranslation } from 'react-i18next'
import {
    Col,
    Row,
} from 'reactstrap'

import { dechex } from '../../../components/shared/dechex'
import {
    TIMED_MODE_WORKDAY_PUMP_TIME_ON,
    TIMED_MODE_WORKDAY_PUMP_TIME_OFF,
    TIMED_MODE_TIMED_MODE_WEEKEND_PUMP_TIME_ON,
    TIMED_MODE_TIMED_MODE_WEEKEND_PUMP_TIME_OFF,
} from '../../../components/shared/device-addresses'
import DevicePumpSettingsInput from './device-pump-settings-input'

import type { DeviceEntity } from './parse-device-data'

interface DeviceDataSetting {
    address: string
    hint: string
    data: string | number
}

interface DevicePumpPlanningProps {
    device: DeviceEntity
    dataSettings?: DeviceDataSetting[]
    className?: string
    deviceWriter?: string[]
}

/**
 * Insert leading zeros into a string until it reaches the desired length.
 */
const zeroPadded = (num: string | number | undefined, place: number): string => {
    const text = num === undefined ? '' : String(num)
    const zero = place - text.length + 1
    return Array(zero > 0 ? zero : 0).join('0') + text
}

/**
 * Format a 4-digit time string by inserting a colon between hours and minutes.
 */
const timeFormatted = (input: string): string => `${input.slice(0, 2)}:${input.slice(2)}`

/**
 * Pump scheduling settings for a single device.
 *
 * Displays the workday / weekend on/off times sourced from the device's
 * raw `dataSettings` collection.
 */
const DevicePumpPlanning = ({ device, dataSettings }: DevicePumpPlanningProps) => {
    const { t } = useTranslation()

    const find = (hint: string): DeviceDataSetting | undefined =>
        dataSettings?.find?.(deviceData => deviceData?.hint === hint)

    const workdayPumpTimeOn = find(TIMED_MODE_WORKDAY_PUMP_TIME_ON)
    const workdayPumpTimeOff = find(TIMED_MODE_WORKDAY_PUMP_TIME_OFF)
    const weekendPumpTimeOn = find(TIMED_MODE_TIMED_MODE_WEEKEND_PUMP_TIME_ON)
    const weekendPumpTimeOff = find(TIMED_MODE_TIMED_MODE_WEEKEND_PUMP_TIME_OFF)

    return (
        <Row className="mt-3">
            <Col lg={6}>
                <span className="my-3 d-block">
                    <i className="fas fa-play fa-lg mr-1" />
                    {t('deviceDetail.work')}
                </span>
                <DevicePumpSettingsInput
                    title={t(`deviceData.hint.${workdayPumpTimeOn?.hint}`)}
                    data={timeFormatted(zeroPadded(dechex(workdayPumpTimeOn?.data), 4))}
                    measurement={t('time')}
                    device={device}
                    dataSetting={workdayPumpTimeOn}
                />
                <DevicePumpSettingsInput
                    title={t(`deviceData.hint.${weekendPumpTimeOn?.hint}`)}
                    data={timeFormatted(zeroPadded(dechex(weekendPumpTimeOn?.data), 4))}
                    measurement={t('time')}
                    device={device}
                    dataSetting={weekendPumpTimeOn}
                />
            </Col>
            <Col lg={6}>
                <span className="my-3 d-block">
                    <i className="fas fa-pause fa-lg mr-1" />
                    {t('deviceDetail.pause')}
                </span>
                <DevicePumpSettingsInput
                    title={t(`deviceData.hint.${workdayPumpTimeOff?.hint}`)}
                    data={timeFormatted(zeroPadded(dechex(workdayPumpTimeOff?.data), 4))}
                    measurement={t('time')}
                    device={device}
                    dataSetting={workdayPumpTimeOff}
                />
                <DevicePumpSettingsInput
                    title={t(`deviceData.hint.${weekendPumpTimeOff?.hint}`)}
                    data={timeFormatted(zeroPadded(dechex(weekendPumpTimeOff?.data), 4))}
                    measurement={t('time')}
                    device={device}
                    dataSetting={weekendPumpTimeOff}
                />
            </Col>
        </Row>
    )
}

export default DevicePumpPlanning
